import type { ReactNode } from 'react';
import { cn } from '../cn.js';
import { Button } from '../components/button.js';
import { IconError } from '../icons.js';

export interface ErrorStateProps {
  readonly title: string;
  readonly description: string;
  /**
   * The correlation id, where the failure carried one.
   *
   * Shown because "quote this reference" is the difference between a support request that can be
   * traced through the logs and one that cannot. It is not a technical detail to hide: it is the
   * only part of a failure a person can usefully carry to somebody else.
   */
  readonly reference?: string | undefined;
  readonly retryLabel?: string;
  readonly onRetry?: (() => void) | undefined;
  readonly action?: ReactNode;
  readonly className?: string;
}

/**
 * Something failed, and what can be done about it.
 *
 * **This replaces two byte-identical files.** `app/error-state.tsx` existed once in the workspace
 * and once in the console, because there was nowhere shared to put it; forty-two files across the
 * two applications handle their own error states besides. The composite takes its strings rather
 * than translating them, so the design system stays product-neutral and the applications keep
 * owning their own language and their own error types — a thin wrapper in each turns an `ApiError`
 * into these props.
 *
 * `role="alert"`, unlike `EmptyState`: a failure is announced, an empty list is not.
 */
export function ErrorState({
  title,
  description,
  reference,
  retryLabel,
  onRetry,
  action,
  className,
}: ErrorStateProps) {
  return (
    <div role="alert" className={cn('flex flex-col items-start gap-(--space-sm)', className)}>
      <p className="flex items-center gap-(--space-sm) text-(length:--font-size-body-large) font-medium text-(--color-text-primary)">
        <IconError aria-hidden="true" className="size-(--size-icon-md) text-(--color-danger)" />
        {title}
      </p>
      <p className="max-w-(--width-container-narrow) text-(length:--font-size-body) text-(--color-text-secondary)">
        {description}
      </p>
      {reference === undefined ? null : (
        <code className="rounded-(--radius-md) bg-(--color-surface-sunken) px-(--space-sm) py-(--space-xs) text-(length:--font-size-caption) text-(--color-text-secondary)">
          {reference}
        </code>
      )}
      {onRetry === undefined && action === undefined ? null : (
        <div className="flex items-center gap-(--space-sm)">
          {onRetry === undefined ? null : (
            <Button onClick={onRetry}>{retryLabel ?? 'Try again'}</Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
